import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { 
  Check, Building, Users, DoorOpen, Car, Mail as MailIcon,
  Shield, Wrench, CreditCard, TrendingDown, MessageSquare,
  Bell, Calendar, Sparkles, ArrowRight, BarChart3, Clock
} from 'lucide-react';

function MultifamilyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const heroFeatures = [
    "Gestión de Residentes",
    "Control de Acceso",
    "Gastos Comunes Automáticos",
    "Ticket de Soporte"
  ];

  const coreModules = [
    {
      icon: Users,
      title: "Gestión de Residentes",
      description: "Base de datos completa de propietarios e inquilinos con historial de pagos y comunicaciones.",
      modules: ["Gestión de Clientes", "Satisfacción", "Mailing Masivo"]
    },
    {
      icon: Calendar,
      title: "Amenities & Espacios Comunes",
      description: "Reserva de quincho, sala de eventos, gimnasio y más. Control de parking para residentes y visitas.",
      modules: ["Gestión de espacios", "Gestión de parking", "Gestión de Visitas"]
    },
    {
      icon: Shield,
      title: "Control de Acceso & Seguridad",
      description: "Acceso con QR, credencial o app móvil. Gestión de visitas y correspondencia integrada.",
      modules: ["Control de acceso", "Gestión de Visitas", "Gestión de correspondencia"]
    },
    {
      icon: Wrench,
      title: "Órdenes Técnicas",
      description: "Sistema de tickets para mantenimiento con trazabilidad completa y aplicación móvil para técnicos.",
      modules: ["Ticket de soporte", "Ordenes técnicas", "Trazabilidad"]
    },
    {
      icon: CreditCard,
      title: "Gastos Comunes Automáticos",
      description: "Facturación recurrente de gastos comunes con notificaciones automáticas y suspensión por mora.",
      modules: ["Facturación recurrente", "Notificaciones automáticas", "Flujo de cobranza"]
    },
    {
      icon: TrendingDown,
      title: "Gestión Financiera",
      description: "Conciliación bancaria, flujo de cobranza y reportes financieros en tiempo real.",
      modules: ["Conciliación bancaria", "Flujo de cobranza", "Reportes financieros"]
    }
  ];

  const benefits = [
    {
      title: "Reduce Morosidad",
      description: "Notificaciones automáticas y flujo de cobranza estructurado. Reduce morosidad hasta en 60%.",
      icon: TrendingDown
    },
    {
      title: "Mejora la Experiencia",
      description: "App móvil para residentes, reserva de amenities online y comunicación fluida. NPS +40%.",
      icon: Sparkles
    },
    {
      title: "Optimiza Operaciones",
      description: "Gestión de tickets, órdenes técnicas y control de acceso centralizado. Ahorra 50 horas mensuales.",
      icon: BarChart3
    },
    {
      title: "Transparencia Total",
      description: "Residentes pueden ver sus gastos comunes, estado de cuenta y documentos en tiempo real.",
      icon: Clock
    }
  ];

  const includedModules = [
    "✅ Gestión de Residentes 360°",
    "✅ Portal de Residentes Web & App",
    "✅ Reserva de Amenities Online",
    "✅ Gestión de Parking",
    "✅ Control de Acceso Inteligente",
    "✅ Gestión de Visitas & QR",
    "✅ Gestión de Correspondencia",
    "✅ Sistema de Tickets & Soporte",
    "✅ Órdenes Técnicas con Trazabilidad",
    "✅ Facturación de Gastos Comunes",
    "✅ Notificaciones Automáticas",
    "✅ Flujo de Cobranza",
    "✅ Conciliación Bancaria",
    "✅ Mailing Masivo",
    "✅ Reportes en Tiempo Real",
    "✅ App Móvil Personalizada"
  ];

  return (
    <>
      <SEOHead
        title="Cloudbase para Multifamily - Gestión Integral de Edificios"
        description="Software completo para administración de edificios: gestión de residentes, gastos comunes, control de acceso, amenities y más."
        keywords="software edificios, gestión multifamily, gastos comunes, administración edificios"
      />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-black relative overflow-hidden min-h-screen flex items-center">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-5xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-full text-sm font-semibold">
                Solución para Multifamily
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight text-white">
                Administra tu Edificio de Forma Inteligente
              </h1>
              
              <p className="text-xl sm:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                Plataforma integral para la gestión de edificios residenciales. Desde control de acceso hasta facturación de gastos comunes, todo en un solo lugar.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Ver Demo Personalizada
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black px-10 py-7 text-base rounded font-semibold transition-all duration-200"
                  onClick={() => window.location.href = '/demo'}
                >
                  Hablar con un Experto
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
                {heroFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Core Modules Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Pack Completo para tu Edificio
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Todo lo que necesitas para administrar tu edificio eficientemente
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {coreModules.map((module, index) => {
                const Icon = module.icon;
                return (
                  <div
                    key={index}
                    className="p-8 bg-gray-50 rounded-lg hover:shadow-lg transition-shadow"
                  >
                    <Icon className="w-12 h-12 text-green-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{module.description}</p>
                    <div className="space-y-2">
                      {module.modules.map((mod, idx) => (
                        <div key={idx} className="text-sm text-green-600 font-medium">
                          • {mod}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Beneficios Medibles
              </h2>
              <p className="text-xl text-gray-600">
                Impacto real en tu administración y satisfacción de residentes
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <Icon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Modules Included */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Todo Incluido en tu Plan
              </h2>
              <p className="text-xl text-gray-600">
                Solución completa sin costos ocultos
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {includedModules.map((module, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
                >
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{module}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para transformar tu edificio?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Agenda una demo y descubre cómo podemos mejorar la gestión de tu edificio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-green-600 hover:bg-gray-100 px-10 py-7 text-base rounded font-semibold transition-all duration-200 inline-flex items-center gap-2"
                onClick={() => window.location.href = '/demo'}
              >
                Agendar Demo Gratuita
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            <p className="mt-8 text-white/70 text-sm">
              Implementación rápida • Portal de residentes incluido • Soporte 24/7
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

export default MultifamilyPage;